import { EmptyPreviewState } from "@/components/features/files/EmptyPreviewState";
import { useFileDuplication } from "@/components/features/files/duplication/FileDuplicationProvider";
import { useRef, useState } from "react";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  CopyPlus,
  Folder,
  FolderInput,
  FolderOpen,
  Lock,
  Pencil,
  Trash2,
} from "lucide-react";
import { useUser } from "@/components/UserProvider";
import {
  Button,
  ContextMenu,
  ContextMenuItem,
  Input,
  LoadingSpinner,
  Text,
} from "@/components/ui";
import { RepoUploadSplitButton } from "@/components/ui/RepoUploadSplitButton/RepoUploadSplitButton";
import { FirmFolderTree } from "@/components/features/settings/firm/FirmFolderTree";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { automationError } from "@/components/features/automations/automationError";
import { libraryQueryKey } from "@/lib/data/react-query/file-library";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  createLibraryFolder,
  uploadLibraryFile,
  renameLibraryItem,
  deleteLibraryItem,
  downloadLibraryItem,
  type LibraryItem,
} from "@/lib/data/requests/file-library";
import { getFileIcon, handleDownload } from "@/lib/utils/matterFile";
import { LibraryContentEditor } from "./LibraryContentEditor";
import {
  PersonalFileDialog,
  type PersonalFilePanel as Panel,
} from "./PersonalFileDialog";
import { useLibraryTree } from "./useLibraryTree";

export function usePersonalFiles({
  enabled,
  onSelect,
}: {
  enabled: boolean;
  onSelect: () => void;
}) {
  const { currentUser, hasPagePermission } = useUser();
  const { openDuplication } = useFileDuplication();
  const canManageSkills = hasPagePermission("skills.personal");
  const tree = useLibraryTree("personal", enabled);
  const [folderId, setFolderId] = useState("");
  const [selectedId, setSelectedId] = useState<string>();
  const [panel, setPanel] = useState<Panel>();
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const folder = tree.itemsById.get(folderId);
  const selected = selectedId ? tree.itemsById.get(selectedId) : undefined;
  const active = selected ?? folder;
  const inSkills = (item: LibraryItem | undefined): boolean => {
    const seen = new Set<string>();
    while (item && !seen.has(item.id)) {
      if (item.systemFolder === "skills" || item.skillId) return true;
      seen.add(item.id);
      item = item.parentId ? tree.itemsById.get(item.parentId) : undefined;
    }
    return false;
  };
  const canChange = (item: LibraryItem) =>
    !item.systemFolder && !item.skillId && !inSkills(item);
  const canMove = (item: LibraryItem) =>
    !!item.fileContentId && !item.skillId && !item.systemFolder;
  const duplicate = (item: LibraryItem) => {
    openDuplication({
      files: [{ id: item.id, name: item.name, isFolder: item.isFolder }],
      repository: { scope: "personal" },
      label: item.name,
    });
  };
  const client = useQueryClient();
  const change = useMutation({
    mutationFn: async (action: () => Promise<unknown>) => action(),
    onError: async (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        await client.invalidateQueries({ queryKey: libraryQueryKey });
        setPanel(undefined);
        toast.error(
          "This file changed. Review the refreshed file and try again.",
        );
      }
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: libraryQueryKey }),
        client.invalidateQueries({ queryKey: [queryKeys.files] }),
      ]);
      if (panel?.kind === "delete") {
        setSelectedId(undefined);
        if (panel.item?.id === folderId) setFolderId(panel.item.parentId ?? "");
      }
      setPanel(undefined);
    },
  });
  const open = (kind: Panel["kind"], item?: LibraryItem) => {
    change.reset();
    setName(item?.name ?? "");
    setPanel({ kind, item });
  };
  const download = useQuery({
    queryKey: [...libraryQueryKey, "preview", selected?.id, selected?.revision],
    queryFn: () => {
      if (!selected) throw new Error("Select a file to preview.");
      return downloadLibraryItem(selected.id);
    },
    enabled: enabled && !!selected && !selected.isFolder && !selected.skillId,
  });
  const downloadFile = (item: LibraryItem) => {
    change.mutate(async () => {
      const result = await downloadLibraryItem(item.id);
      await handleDownload(result.url, result.fileName);
    });
  };
  const navigate = (id: string) => {
    onSelect();
    tree.loadFolder(id);
    setFolderId(id);
    setSelectedId(undefined);
    setSearch("");
  };
  const shownItems = search.trim()
    ? tree.items.filter((item) => {
        // Keep ancestor folders visible; search only metadata already loaded.
        return (
          item.isFolder ||
          item.name.toLowerCase().includes(search.trim().toLowerCase())
        );
      })
    : tree.items;
  const folderFooter = (id: string) => {
    const state = tree.folderState(id);
    return (
      <div className="px-4">
        {state.loading && (
          <p role="status" className="text-muted-foreground py-2 text-sm">
            Loading files…
          </p>
        )}
        {state.error && (
          <div role="alert" className="py-2 text-sm">
            {automationError(state.error)}
            <Button variant="ghost" size="sm" onClick={state.retry}>
              Try again
            </Button>
          </div>
        )}
        {state.hasMore && (
          <Button
            variant="ghost"
            size="sm"
            disabled={state.loading}
            onClick={state.loadMore}
          >
            Load more
          </Button>
        )}
      </div>
    );
  };
  const HeaderIcon = selected ? getFileIcon(selected.name) : Folder;
  const currentFolderState = tree.folderState(folderId);
  const rootFolderState = tree.folderState("");
  const hasChildren = tree.items.some(
    (item) => (item.parentId ?? "") === folderId,
  );

  return {
    atRoot: !folderId && !selectedId,
    reset: () => {
      navigate("");
    },
    toolbar: (
      <div className="border-b px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          {inSkills(folder) ? (
            canManageSkills && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  open("createSkill");
                }}
              >
                Create skill
              </Button>
            )
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  open("folder");
                }}
              >
                <Folder className="mr-2 h-4 w-4" />
                New folder
              </Button>
              <RepoUploadSplitButton
                onUploadFile={() => input.current?.click()}
                disabled={change.isPending}
                loading={change.isPending}
                options={[]}
              />
            </>
          )}
        </div>
        <Input
          className="mt-3"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder="Filter loaded files..."
          aria-label="Filter my files"
        />
        {change.error && (
          <p role="alert" className="text-destructive mt-2 text-sm">
            {automationError(change.error)}
          </p>
        )}
      </div>
    ),
    tree:
      rootFolderState.loading || rootFolderState.error ? (
        folderFooter("")
      ) : (
        <FirmFolderTree
          files={shownItems.map((item) => ({
            id: item.id,
            name: item.name,
            isFolder: item.isFolder,
            idLocation: item.parentId ?? "",
            firmId: currentUser.firmId,
            dateCreated: "",
            dateModified: item.dateModified ?? "",
          }))}
          currentFolderId={folderId}
          onNavigateToFolder={navigate}
          onExpandFolder={tree.loadFolder}
          unloadedFolderIds={tree.unloadedFolderIds}
          renderFolderFooter={folderFooter}
          showRoot={false}
          showFiles
          selectedFileId={selectedId}
          isSearching={!!search.trim()}
          onOpenFile={(item) => {
            onSelect();
            setFolderId(item.idLocation ?? "");
            setSelectedId(item.id);
          }}
          renderItemSuffix={(item) =>
            tree.itemsById.get(item.id)?.systemFolder ? (
              <Lock
                aria-label="Default folder"
                className="text-muted-foreground size-3 shrink-0"
              />
            ) : null
          }
          renderContextMenu={(row, trigger) => {
            const item = tree.itemsById.get(row.id);
            if (!item) return <>{trigger}</>;
            return (
              <ContextMenu
                entityName={item.isFolder ? "folder" : "file"}
                trigger={trigger}
                open={
                  item.skillId && canManageSkills
                    ? {
                        label: "Edit skill",
                        onSelect: () => {
                          open("content", item);
                        },
                      }
                    : canChange(item)
                      ? {
                          label: "Rename",
                          onSelect: () => {
                            open("rename", item);
                          },
                        }
                      : false
                }
                delete={
                  canChange(item)
                    ? {
                        onSelect: () => {
                          open("delete", item);
                        },
                      }
                    : false
                }
                copy={{ label: "Copy name", value: item.name }}
              >
                {canMove(item) && (
                  <ContextMenuItem
                    onSelect={() => {
                      open("move", item);
                    }}
                  >
                    <FolderInput />
                    Move file
                  </ContextMenuItem>
                )}
                {canChange(item) && (
                  <ContextMenuItem
                    onSelect={() => {
                      duplicate(item);
                    }}
                  >
                    <CopyPlus />
                    Duplicate {item.isFolder ? "folder" : "file"}
                  </ContextMenuItem>
                )}
                {!item.isFolder && !item.skillId && (
                  <ContextMenuItem
                    onSelect={() => {
                      downloadFile(item);
                    }}
                  >
                    <Download />
                    Download file
                  </ContextMenuItem>
                )}
              </ContextMenu>
            );
          }}
        />
      ),
    previewHeader: (
      <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <HeaderIcon
            className={
              "h-5 w-5 shrink-0 " +
              (selected ? "text-blue-500" : "text-sky-400")
            }
          />
          <Text
            variant="subHeading"
            color="foreground"
            className="mb-0 block min-w-0 flex-1 truncate text-nowrap break-normal"
            title={active?.name ?? "My Files"}
          >
            {active?.name ?? "My Files"}
          </Text>
          {active?.systemFolder && (
            <Lock
              aria-label="Default folder"
              className="text-muted-foreground size-3"
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          {active?.skillId && canManageSkills && (
            <Button
              variant="ghost"
              size="icon"
              title="Edit skill"
              onClick={() => {
                open("content", active);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {active && canChange(active) && (
            <Button
              variant="ghost"
              size="icon"
              title="Rename"
              onClick={() => {
                open("rename", active);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {selected && canMove(selected) && (
            <Button
              variant="ghost"
              size="icon"
              title="Move file"
              onClick={() => {
                open("move", selected);
              }}
            >
              <FolderInput className="h-4 w-4" />
            </Button>
          )}
          {active && canChange(active) && (
            <Button
              variant="ghost"
              size="icon"
              title={`Duplicate ${active.isFolder ? "folder" : "file"}`}
              onClick={() => {
                duplicate(active);
              }}
            >
              <CopyPlus className="h-4 w-4" />
            </Button>
          )}
          {selected && !selected.skillId && (
            <Button
              variant="ghost"
              size="icon"
              title="Download file"
              onClick={() => {
                downloadFile(selected);
              }}
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          {active && canChange(active) && (
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={() => {
                open("delete", active);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    ),
    previewBody: selected?.skillId ? (
      <div className="p-6">
        {canManageSkills ? (
          <LibraryContentEditor
            key={selected.id}
            id={selected.id}
            skillId={selected.skillId}
            onClose={() => {
              setSelectedId(undefined);
            }}
          />
        ) : (
          <p>You do not have permission to edit this skill.</p>
        )}
      </div>
    ) : selected ? (
      download.isPending ? (
        <div role="status" className="flex h-full items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : download.error ? (
        <p role="alert" className="p-6">
          {automationError(download.error)}
        </p>
      ) : (
        <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
          <FileViewer
            key={selected.id}
            file={{ fileName: selected.name, presignedURLKey: "" }}
            fileType="firm-file"
            presignedUrl={download.data.url}
            showDownload={false}
            showTitle={false}
            footerClassName="hidden"
            viewerClassName="bg-card min-h-0 items-stretch justify-stretch"
            wrapperClassName="bg-card min-h-0 rounded-none shadow-none"
            compactDocxPreview
          />
        </div>
      )
    ) : (
      <EmptyPreviewState
        title={folder?.name ?? "My Files"}
        icon={folder ? FolderOpen : Folder}
        description={
          currentFolderState.error
            ? automationError(currentFolderState.error)
            : currentFolderState.loading
              ? "Loading files…"
              : hasChildren
                ? "Select a file to preview."
                : "This folder is empty."
        }
      />
    ),
    dialogs: (
      <>
        <input
          ref={input}
          type="file"
          className="hidden"
          aria-label="Upload to my files"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file)
              change.mutate(() =>
                uploadLibraryFile(file, "personal", folderId || undefined),
              );
          }}
        />
        <PersonalFileDialog
          panel={panel}
          name={name}
          setName={setName}
          pending={change.isPending}
          error={change.error}
          onClose={() => {
            if (panel?.kind === "move") setSelectedId(undefined);
            setPanel(undefined);
          }}
          onSubmit={() => {
            change.mutate(() =>
              panel?.kind === "folder"
                ? createLibraryFolder("personal", folderId || undefined, name)
                : panel?.item
                  ? panel.kind === "delete"
                    ? deleteLibraryItem(panel.item)
                    : renameLibraryItem(panel.item, name)
                  : Promise.resolve(),
            );
          }}
        />
      </>
    ),
  };
}
